import { useState } from "react";
import { AlertTriangle, Trash2, CheckCircle2 } from "lucide-react";
import { BACKEND_URL } from "@/lib/auth";

type PreviewResp = {
    stage: "preview";
    projects_count: number;
    media_count: number;
    requests_count: number;
    estimated_r2_mb: number;
    sample_projects: { slug: string; title: string }[];
};

type ExecutedResp = {
    stage: "executed";
    projects_deleted: number;
    media_deleted: number;
    requests_deleted: number;
    r2_files_deleted: number;
    r2_errors: number;
};

const CONFIRM_PHRASE = "TESTDATEN LÖSCHEN";

export default function PurgeDemoDataCard() {
    const [busy, setBusy] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [preview, setPreview] = useState<PreviewResp | null>(null);
    const [typed, setTyped] = useState("");
    const [result, setResult] = useState<ExecutedResp | null>(null);
    const [error, setError] = useState<string>("");

    const openPreview = async () => {
        setBusy(true);
        setError("");
        try {
            const res = await fetch(`${BACKEND_URL}/api/admin/system/purge-demo-data`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ confirm: false }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
            setPreview(data);
            setResult(null);
            setTyped("");
            setModalOpen(true);
        } catch (e) {
            setError((e as Error).message);
        } finally {
            setBusy(false);
        }
    };

    const execute = async () => {
        if (typed.trim() !== CONFIRM_PHRASE) return;
        setBusy(true);
        setError("");
        try {
            const res = await fetch(`${BACKEND_URL}/api/admin/system/purge-demo-data`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ confirm: true, typed_confirmation: CONFIRM_PHRASE }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
            setResult(data);
        } catch (e) {
            setError((e as Error).message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <div
                className="p-6 rounded-sm"
                style={{
                    background: "hsl(var(--hf-bg))",
                    border: "1px solid hsl(45 90% 60% / 0.4)",
                }}
                data-testid="dashboard-purge-card"
            >
                <div className="flex items-start gap-4">
                    <span
                        className="inline-flex items-center justify-center w-10 h-10 rounded-sm shrink-0"
                        style={{ background: "hsl(45 90% 60%)", color: "hsl(var(--hf-ink))" }}
                    >
                        <AlertTriangle size={18} />
                    </span>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-display text-lg">Testdaten &amp; Setup</h3>
                        <p className="text-sm text-hf-muted mt-1 mb-3">
                            Aktuell sind Demo-Projekte, -Medien und -Kontaktanfragen in der Datenbank. Entferne sie vor dem Livegang mit einem Klick — inklusive R2-Dateien und Kontakt-Anhängen.
                        </p>
                        <button
                            type="button"
                            onClick={() => void openPreview()}
                            disabled={busy}
                            className="hf-btn hf-btn-primary"
                            style={{
                                background: "hsl(var(--hf-danger))",
                                borderColor: "hsl(var(--hf-danger))",
                                color: "white",
                            }}
                            data-testid="dashboard-purge-open"
                        >
                            <Trash2 size={13} /> Alle Demo-Inhalte entfernen
                        </button>
                        {error && (
                            <div className="text-xs mt-2" style={{ color: "hsl(var(--hf-danger))" }}>{error}</div>
                        )}
                    </div>
                </div>
            </div>

            {modalOpen && preview && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center px-4"
                    style={{ background: "hsl(var(--hf-ink) / 0.78)" }}
                    onClick={() => !busy && setModalOpen(false)}
                    data-testid="dashboard-purge-modal"
                >
                    <div
                        className="max-w-xl w-full rounded-sm p-6"
                        style={{ background: "hsl(var(--hf-paper))", border: "1px solid hsl(var(--hf-line))" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {!result ? (
                            <>
                                <h3 className="font-display text-2xl mb-2">Demo-Inhalte endgültig entfernen?</h3>
                                <p className="text-sm text-hf-muted mb-4">
                                    Es werden folgende Einträge <strong>unwiderruflich</strong> gelöscht (inkl. R2-Files):
                                </p>
                                <div
                                    className="mb-4 p-4 rounded-sm text-sm space-y-1"
                                    style={{ background: "hsl(var(--hf-surface))" }}
                                >
                                    <div>· <strong>{preview.projects_count}</strong> Projekte</div>
                                    <div>· <strong>{preview.media_count}</strong> Medien ({preview.estimated_r2_mb} MB in R2)</div>
                                    <div>· <strong>{preview.requests_count}</strong> Kontaktanfragen</div>
                                    {preview.sample_projects.length > 0 && (
                                        <div className="mt-2 pt-2 border-t" style={{ borderColor: "hsl(var(--hf-line))" }}>
                                            <div className="text-xs text-hf-muted mb-1">Beispiel-Projekte:</div>
                                            {preview.sample_projects.map((p) => (
                                                <div key={p.slug} className="text-xs">— {p.title} <span className="text-hf-muted">({p.slug})</span></div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <label className="block text-sm mb-1">
                                    Zum Bestätigen bitte <code className="font-mono">{CONFIRM_PHRASE}</code> eintippen:
                                </label>
                                <input
                                    type="text"
                                    value={typed}
                                    onChange={(e) => setTyped(e.target.value)}
                                    className="w-full px-3 py-2 rounded-sm text-sm mb-4"
                                    style={{ background: "hsl(var(--hf-bg))", border: "1px solid hsl(var(--hf-line))", fontFamily: "ui-monospace, monospace" }}
                                    data-testid="dashboard-purge-input"
                                    autoFocus
                                />
                                <div className="flex justify-end gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setModalOpen(false)}
                                        disabled={busy}
                                        className="hf-btn hf-btn-ghost"
                                        data-testid="dashboard-purge-cancel"
                                    >
                                        Abbrechen
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void execute()}
                                        disabled={busy || typed.trim() !== CONFIRM_PHRASE}
                                        className="hf-btn"
                                        style={{
                                            background: typed.trim() === CONFIRM_PHRASE ? "hsl(var(--hf-danger))" : "hsl(var(--hf-line))",
                                            color: typed.trim() === CONFIRM_PHRASE ? "white" : "hsl(var(--hf-muted))",
                                            border: `1px solid ${typed.trim() === CONFIRM_PHRASE ? "hsl(var(--hf-danger))" : "hsl(var(--hf-line))"}`,
                                            cursor: typed.trim() === CONFIRM_PHRASE ? "pointer" : "not-allowed",
                                        }}
                                        data-testid="dashboard-purge-confirm"
                                    >
                                        <Trash2 size={13} /> Endgültig löschen
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="flex items-center gap-3 mb-3">
                                    <CheckCircle2 size={24} style={{ color: "hsl(140 45% 45%)" }} />
                                    <h3 className="font-display text-2xl">Testdaten entfernt</h3>
                                </div>
                                <div className="text-sm space-y-1 mb-4">
                                    <div>{result.projects_deleted} Projekte gelöscht</div>
                                    <div>{result.media_deleted} Medien gelöscht ({result.r2_files_deleted} R2-Files)</div>
                                    <div>{result.requests_deleted} Kontaktanfragen gelöscht</div>
                                    {result.r2_errors > 0 && (
                                        <div style={{ color: "hsl(var(--hf-danger))" }}>{result.r2_errors} R2-Fehler beim Löschen</div>
                                    )}
                                </div>
                                <div className="flex justify-end">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setModalOpen(false);
                                            window.location.reload();
                                        }}
                                        className="hf-btn hf-btn-primary"
                                        data-testid="dashboard-purge-done"
                                    >
                                        OK
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
